/**
 * SearchDetailKpis — the six headline numbers, in the v38 prototype's order.
 *
 * Per-search only. The product guardrail is "focus on one search at a time", so
 * nothing here aggregates across searches — every figure is this search's own.
 *
 * `avg_days_in_process` renders an em dash when the backend sends `null`, which
 * it does when no candidacy has a usable date pair. Showing 0 there would read as
 * "everyone was processed instantly", which is the opposite of "we cannot tell".
 */

import type { SearchReportKpis } from "@nu-assist/contracts";

export interface SearchDetailKpisProps {
  kpis: SearchReportKpis;
}

interface Kpi {
  label: string;
  value: string;
  /** Accent class for the number, or undefined for the default text colour. */
  tone?: string;
  testId: string;
}

export function SearchDetailKpis({ kpis }: SearchDetailKpisProps) {
  const cards: Kpi[] = [
    {
      label: "Total candidates",
      value: String(kpis.total_candidacies),
      testId: "kpi-total",
    },
    {
      label: "Interview rate",
      value: `${kpis.interview_rate_pct}%`,
      tone: "text-accent-ink",
      testId: "kpi-interview-rate",
    },
    {
      label: "Avg days in process",
      value:
        kpis.avg_days_in_process === null || kpis.avg_days_in_process === undefined
          ? "—"
          : String(kpis.avg_days_in_process),
      testId: "kpi-avg-days",
    },
    {
      label: "Pursuing",
      value: String(kpis.pursuing_count),
      tone: "text-warning-ink",
      testId: "kpi-pursuing",
    },
    {
      label: "Sourced",
      value: String(kpis.sourced_count),
      testId: "kpi-sourced",
    },
    {
      label: "Placed",
      value: String(kpis.placed_count),
      tone: "text-accent-ink",
      testId: "kpi-placed",
    },
  ];

  return (
    <dl
      data-testid="search-detail-kpis"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
    >
      {cards.map((card) => (
        <div
          key={card.testId}
          data-testid={card.testId}
          className="rounded-md border border-border bg-surface px-4 py-3"
        >
          <dd className={`text-h1 ${card.tone ?? "text-text"}`}>{card.value}</dd>
          <dt className="mt-1 text-caption text-text-secondary">{card.label}</dt>
        </div>
      ))}
    </dl>
  );
}
